import React, { useEffect, useRef } from 'react';
import { X, BookOpen } from 'lucide-react';
import './pie.css';

/**
 * PIEGlossaryModal — plain-language explainer for the abstract LIE
 * terms (PE, C, M, ROE, LEI). Most users seeing PIE for the first
 * time have never met these acronyms, and the words you can pick from
 * a glossary are exactly the words people repeat back to peers — so
 * this is also marketing surface, not just docs.
 *
 * Pass `focus` to scroll-and-highlight a specific term on open.
 */
const TERMS = [
  {
    key: 'pe',
    label: 'PE',
    full: 'Personal Effectiveness',
    short: 'How effective you actually are in your role, right now.',
    body: (
      <>
        <p>
          Your Personal Effectiveness is a single number — between 0% and 100% — that captures how much
          of your full potential you&apos;re putting on the line in your role today.
        </p>
        <p>
          It&apos;s built from two things multiplied together: <strong>Capability</strong>{' '}
          (do you have the skills?) and <strong>Motivation</strong> (do you want to use them?).
          We multiply because real performance breaks if either one drops — a 99% capable person
          who is 0% motivated is still 0% effective.
        </p>
        <div className="pie-glossary-formula">PE = Capability × Motivation</div>
      </>
    ),
  },
  {
    key: 'capability',
    label: 'C',
    full: 'Capability',
    short: 'Whether you have the skills your role needs.',
    body: (
      <>
        <p>
          Capability is the average of your skill levels compared to the levels your role
          requires. If your role needs 4.0 in Negotiation and you&apos;re assessed at 4.6,
          that&apos;s 115% — you exceed the bar.
        </p>
        <p>
          Most people have a few skills above the bar and a few below. The score blends them
          into one number so you can see your overall fit at a glance, then drill into the
          skill profile below to see where the gaps are.
        </p>
      </>
    ),
  },
  {
    key: 'motivation',
    label: 'M',
    full: 'Motivation',
    short: 'How much you want to do the work — and how supported you feel doing it.',
    body: (
      <>
        <p>
          Motivation isn&apos;t a vibe — it&apos;s a measurable mix of three things:
        </p>
        <ul>
          <li><strong>Intrinsic</strong> — do you care about the work itself?</li>
          <li><strong>Extrinsic</strong> — does the comp / reward / recognition work for you?</li>
          <li><strong>Manager support</strong> — does your manager have your back?</li>
        </ul>
        <p>
          Even a highly skilled person can underperform if any of these slip too low.
          That&apos;s why a 5%-point shift in Motivation often beats a 5%-point gain in
          Capability — it lifts everything you do.
        </p>
      </>
    ),
  },
  {
    key: 'lei',
    label: 'LEI',
    full: 'Personal Learning Effectiveness Index',
    short: 'How well learning actually changes what you do at work.',
    body: (
      <>
        <p>
          LEI scores the quality of the learning loop in your role — out of 100. It blends signals
          like manager rating, behavior change, retention, and how often what you learn is actually
          applied on the job.
        </p>
        <p>
          A high LEI means training translates into real performance. A low LEI means time
          spent learning isn&apos;t showing up in your work — usually a signal something else
          is missing (manager backing, time, or relevance).
        </p>
      </>
    ),
  },
  {
    key: 'roe',
    label: 'ROE',
    full: 'Return on Expectation',
    short: 'Did the learning actually lift your performance — and can you (and others) see it?',
    body: (
      <>
        <p>
          Return on Expectation asks the most important question about any learning you do:
          did it result in a <strong>meaningful improvement in your performance</strong> — through
          developing your <strong>capability</strong> and your <strong>motivation</strong> to perform?
        </p>
        <p>
          And critically: is that change something <em>you</em> have seen in yourself, and that
          <em> others</em> (your manager, peers, customers) have seen in you? It&apos;s measured by combining
          survey signals (your reflections, manager rating, behavior change) with hard performance data
          (the KPIs your role moves).
        </p>
        <p>
          When you simulate an action in <em>What if</em>, the predicted ROE estimates that lift as a
          percentage and a category (Low / Moderate / High). When you commit and come back to
          &ldquo;Did it work?&rdquo;, we compare predicted vs realized — that&apos;s how the loop closes.
        </p>
      </>
    ),
  },
];

export default function PIEGlossaryModal({ open, focus, onClose }) {
  const scrollRef = useRef(null);
  const focusedRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  useEffect(() => {
    if (open && focus && focus !== 'overview' && focusedRef.current) {
      focusedRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (open && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [open, focus]);

  if (!open) return null;

  return (
    <div className="pie-modal-backdrop" onClick={onClose} role="presentation" data-testid="pie-glossary-modal">
      <div
        className="pie-modal pie-modal-glossary"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="pie-glossary-title"
      >
        <div className="pie-modal-head">
          <h3 id="pie-glossary-title" className="pie-modal-title">
            <BookOpen size={14} aria-hidden style={{ marginRight: 6, verticalAlign: -2 }} />
            What these terms mean
          </h3>
          <button
            type="button"
            className="pie-modal-close"
            onClick={onClose}
            aria-label="Close"
            data-testid="pie-glossary-close"
          >
            <X size={14} />
          </button>
        </div>
        <div className="pie-modal-body" ref={scrollRef}>
          {focus === 'overview' && (
            <p className="pie-glossary-intro">
              Your Seertech Mirror dashboard uses five key terms. None of them are jargon —
              they&apos;re plain-language ways to describe how you&apos;re doing and where to focus.
            </p>
          )}
          {TERMS.map((t) => {
            const isFocus = focus === t.key;
            return (
              <div
                key={t.key}
                ref={isFocus ? focusedRef : null}
                className={`pie-glossary-term ${isFocus ? 'pie-glossary-term-focus' : ''}`}
                data-testid={`pie-glossary-term-${t.key}`}
              >
                <div className="pie-glossary-term-head">
                  <span className="pie-glossary-term-badge pie-mono">{t.label}</span>
                  <div>
                    <div className="pie-glossary-term-full">{t.full}</div>
                    <div className="pie-glossary-term-short">{t.short}</div>
                  </div>
                </div>
                <div className="pie-glossary-term-body">{t.body}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
